import { mergeDeep } from '../../utils/object';
import { BodyData, Layer, Method, Middleware, HttpClientOptions } from './http-client.interfaces';
import { toJSON } from './to-json';
import { constructFullPath } from './construct-full-path';
import { constructBodyData } from './construct-body-data';
import { getContentType } from './get-content-type';

const { assign } = Object;

type ErrorHandler = (err: unknown) => void;
type Listener = (req: Request, res: Response) => void;

export interface HttpClientErrorOptions {
  code: string;
  message: string;
  status: number;
  description?: string;
}

export class HttpClientError extends Error {
  public code: string;
  public message: string;
  public status: number;
  public description: string | undefined;

  constructor({ code, message, status, description }: HttpClientErrorOptions) {
    super(message);

    this.code = code;
    this.message =
      process.env.NODE_ENV === 'development' && description !== undefined ? `${message} ${description}` : message;
    this.status = status;
    this.description = description;

    Object.setPrototypeOf(this, HttpClientError.prototype);
  }
}

export type HttpClientBaseUrl = string | (() => string);

export class HttpClient {
  public baseUrl: HttpClientBaseUrl;
  public options: HttpClientOptions & { headers: HeadersInit };

  protected errorHandlers: Set<ErrorHandler> = new Set();
  protected listeners: Set<Listener> = new Set();
  protected stack: Layer;

  constructor(baseUrl: HttpClientBaseUrl, options?: HttpClientOptions, stack?: Layer) {
    this.baseUrl = baseUrl;
    this.options = assign({ headers: {} }, options);

    if (stack) {
      this.stack = stack.bind(this);
    } else {
      this.stack = (req: Request) => fetch(req);
    }
  }

  public static create(url?: string, options?: HttpClientOptions) {
    return new HttpClient(url ?? '', options);
  }

  public use(middleware: Middleware) {
    this.stack = middleware(this.stack);
  }

  public subscribe(listener: Listener) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  public onError(handler: ErrorHandler) {
    this.errorHandlers.add(handler);
    return () => {
      this.errorHandlers.delete(handler);
    };
  }

  public setHeader(name: string, value: string) {
    assign(this.options.headers, { [name]: value });
  }

  public setBearer(token: string) {
    this.setHeader('Authorization', `Bearer ${token}`);
  }

  public clone(pathname: string = '') {
    // @ts-ignore
    return new this.constructor(
      pathname ? `${this.baseUrl}/${pathname}` : this.baseUrl,
      { ...this.options },
      this.stack,
    );
  }

  public async get<Res>(path: string, options?: RequestInit) {
    return this.request<Res>('GET', path, undefined, options);
  }

  public async post<Res, Req extends BodyData = BodyData>(path: string, data?: Req, options?: RequestInit) {
    return this.request<Res>('POST', path, data, options);
  }
  public async delete<Res, Req extends BodyData = BodyData>(path: string, data?: Req, options?: RequestInit) {
    return this.request<Res>('DELETE', path, data, options);
  }

  public async put<Res, Req extends BodyData = BodyData>(path: string, data?: Req, options?: RequestInit) {
    return this.request<Res>('PUT', path, data, options);
  }

  public async patch<Res, Req extends BodyData = BodyData>(path: string, data?: Req, options?: RequestInit) {
    return this.request<Res>('PATCH', path, data, options);
  }

  protected async request<Res>(method: Method, path: string, data: BodyData, options?: RequestInit) {
    // Init
    const init: RequestInit = mergeDeep({}, this.options, options, {
      method,
    });

    init.headers ??= {};
    init.headers['Content-Type'] = getContentType(data);

    if (data && method !== 'GET') {
      init.body = constructBodyData(data);
    }

    const baseUrl = typeof this.baseUrl === 'string' ? this.baseUrl : this.baseUrl();

    // Request
    const req = new Request(constructFullPath(baseUrl, path), init);

    // Response
    try {
      const res = await this.run(req);

      return toJSON<Res>(res);
    } catch (err) {
      this.emitError(err);

      throw err;
    }
  }

  protected emit(req: Request, res: Response) {
    this.listeners.forEach((listener) => {
      listener(req, res);
    });
  }

  protected emitError(err: unknown) {
    this.errorHandlers.forEach((handler) => {
      handler(err);
    });
  }

  protected run(req: Request) {
    return this.stack(req);
  }
}
